import { StandardEvents } from '@shopify/events';

/**
 * Keeps Blankloom's custom BAG count in sync with AJAX cart mutations.
 *
 * The stock Horizon header uses <cart-icon>, but the bespoke Blankloom
 * header renders its own counter. Listening to the same standard cart event
 * lets both product-form additions and cart-page removals update immediately.
 */
class BlankloomCartCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(async (result) => {
        const eventCount = this.#getEventCount(result);
        const count = eventCount ?? (await this.#fetchCartCount());

        this.#render(count, true);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[blankloom-cart-count] Cart update failed:', error);
        }
      });
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.#fetchCartCount()
      .then((count) => this.#render(count, false))
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[blankloom-cart-count] Cart refresh failed:', error);
        }
      });
  };

  /**
   * @param {{cart?: {totalQuantity?: number}, detail?: {itemCount?: number}}} result
   * @returns {number | null}
   */
  #getEventCount(result) {
    const rawCount = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
    const count = Number(rawCount);

    return Number.isFinite(count) && count >= 0 ? count : null;
  }

  /** @returns {Promise<number>} */
  async #fetchCartCount() {
    const cartUrl = globalThis.Theme?.routes?.cart_url ?? '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    const cart = await response.json();
    const count = Number(cart.item_count);

    return Number.isFinite(count) && count >= 0 ? count : 0;
  }

  /**
   * @param {number} count
   * @param {boolean} announce
   */
  #render(count, announce) {
    const countElement = this.querySelector('[data-blankloom-cart-count]');
    const cartLink = this.querySelector('[data-blankloom-cart-link]');
    const liveRegion = this.querySelector('[data-blankloom-cart-live-region]');
    const itemLabel = count === 1 ? 'item' : 'items';

    if (countElement) countElement.textContent = String(count);
    if (cartLink) cartLink.setAttribute('aria-label', `Cart, ${count} ${itemLabel}`);
    if (announce && liveRegion) liveRegion.textContent = `Cart updated: ${count} ${itemLabel}`;

    this.dataset.count = String(count);
  }
}

if (!customElements.get('blankloom-cart-count')) {
  customElements.define('blankloom-cart-count', BlankloomCartCount);
}
